import { useMemo } from 'react';

import {
	type default as AnalyticsWebClient,
	tenantType,
	userType,
} from '@atlassiansox/analytics-web-client';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useAnalyticsAttributes } from './context';

type ErrorDetails = {
	message: string;
	stack?: string;
};

export type AnalyticsAttributes = Record<
	string,
	string | number | boolean | ErrorDetails | undefined | object
>;

type AnalyticsEmit = (
	actionSubject: string,
	action: string,
	attributes?: AnalyticsAttributes,
) => void;

export type AnalyticsApi = {
	track: AnalyticsEmit;
	ui: AnalyticsEmit;
	operational: AnalyticsEmit;
};

export const useAnalytics = (): AnalyticsApi => {
	const baseAnalyticsAttributes = useAnalyticsAttributes();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return useMemo<AnalyticsApi>(() => {
		const fire =
			(eventType: 'track' | 'ui' | 'operational'): AnalyticsEmit =>
			(actionSubject, action, attributes) => {
				createAnalyticsEvent({
					action,
					actionSubject,
					eventType,
					tags: ['townsquare'],
					attributes: { ...baseAnalyticsAttributes, ...attributes },
				}).fire('atlas');
			};

		return {
			track: fire('track'),
			ui: fire('ui'),
			operational: fire('operational'),
		};
	}, [baseAnalyticsAttributes, createAnalyticsEvent]);
};

/*
 * NOTE: at least one of accountId and cloudId must be provided in order for analytics
 * to be sent successfully.
 */
export function setAnalyticsClientInfo(
	analyticsClient: AnalyticsWebClient,
	subproduct: 'townsquare-goals' | 'townsquare-projects',
	accountId?: string,
	orgId?: string,
	cloudId?: string,
): void {
	analyticsClient.setSubproduct(subproduct);

	if (orgId && orgId !== '') {
		analyticsClient.setOrgInfo(orgId);
	}

	if (cloudId && cloudId !== '') {
		analyticsClient.setTenantInfo(tenantType.CLOUD_ID, cloudId);
	} else {
		analyticsClient.setTenantInfo(tenantType.NONE, cloudId);
	}

	if (accountId && accountId !== '') {
		analyticsClient.setUserInfo(userType.ATLASSIAN_ACCOUNT, accountId);
	}
}
